import { useCallback, useEffect, useMemo, useRef, useState, type PropsWithChildren } from 'react'
import {
  collection,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  query,
  runTransaction,
  serverTimestamp,
  setDoc,
  Timestamp,
  updateDoc,
  where,
} from 'firebase/firestore'
import { firestoreDb } from '../services/firebase'
import {
  createActiveOwnerMembership,
  getMembershipRef,
  normalizeMemberEmail,
} from '../services/memberService'
import { useAuth } from './useAuth'
import { useDisplayName } from '../hooks/useDisplayName'
import {
  CHECKLIST_INPUT_HARD_LIMITS,
  clampIntegerToLimit,
  normalizeChecklistInputLimits,
  type ChecklistInputLimits,
} from '../utils/checklistLimits'
import { AVAILABLE_VIEW_KEYS, DASHBOARD_VIEW_KEYS, type DashboardViewKey, type WorkspaceLimits } from '../types/schema'
import {
  createDefaultWorkspaceLimits,
  limitsToChecklistInputLimits,
  normalizeWorkspaceLimits,
} from '../utils/workspaceLimits'
import { suggestWorkspaceSlug, validateWorkspaceSlug } from '../utils/workspaceSlug'
import {
  DEFAULT_WORKSPACE_ACTION_PERMISSIONS,
  isWorkspaceRole,
  normalizeWorkspaceActionPermissions,
  roleCanPerform,
  type WorkspaceAction,
} from '../utils/workspacePermissions'
import { isWorkspaceOwner } from '../utils/workspaceOwners'
import { useNls } from '../nls/nlsProvider'
import { ControlNotice, Button } from '../components/Common'
import { restoreWorkspaceDeletion } from '../services/workspaceDeletionService'
import { formatPurgeDate } from '../utils/purgeDate'
import { normalizeChecklistBlockedTodoPolicy } from '../utils/checklistBlockedTodoPolicy'
import { isHelperOnlyAccount } from '../utils/helperMembership'
import { normalizeRaidBlueprint } from '../utils/raidBlueprint'
import { normalizeWorkspaceSurfaceMask } from '../types/workspaceSurfaceMask'
import { detectLocaleCurrency } from '../utils/localeCurrency'
import { detectDeviceCountry } from '../utils/localeCountry'
import {
  registerWorkspaceCommitter,
  registerWorkspaceProviderBridge,
  requestWorkspaceSwitch,
} from '../services/workspaceSwitchService'
import { safeWorkspaceLanding, resolveSwitchLanding, type SwitchLandingApp } from '../services/workspaceSwitch/switchLanding'
import { subscribeToMyMemberships } from '../services/membershipLiveService'
import { promptWorkspaceAccessLost, getAccessLostRequest } from '../services/workspaceSwitch/accessLostPrompt'
import { getRecentWorkspaceIds } from '../services/workspaceSwitch/recentWorkspaces'
import { emitToast } from './ToastContext'
import { getNlsString } from '../nls/nlsRuntime'
import { formatRoleLabel } from '../utils/roleLabels'
import { getWrapperRole } from '../utils/wrapperRole'
import { markHelperOutboxWorkspaceAccessLost } from '../services/helper/helperOutboxDb'
import { useOpsWorkspaceOfflineLimit } from './useOpsWorkspaceOfflineLimit'
import {
  ACTIVE_WORKSPACE_STORAGE_KEY,
  ACTIVE_WORKSPACE_TAB_STORAGE_KEY,
  APP_MODE_STORAGE_KEY,
  WorkspaceContext,
  clearWorkspaceStorage,
  type WorkspaceContextValue,
} from './workspaceContextCore'
import { getConsoleParts } from '../utils/consoleRoutes'
import { usePersistedState } from '../hooks/usePersistedState'
import { normalizeTierId } from '../config/PricingConfig'
import { usePlanLimitOverrides } from '../services/planLimitsService'
import { testingOverrideAllowsAllFeatures } from '../config/testingOverrides'
import { MINIMALIST_AUDIT_CATEGORIES, normalizeAuditCategories } from '../services/auditCategories'
import {
  EXPLORE_PREVIEW_TTL_MS,
  readRecentExplorePreviewSeeds,
  rememberExplorePreviewSeed,
  toPreviewWorkspace,
} from '../services/exploreWorkspaceSeeds'
import { isPersonalWorkspace, isTeamWorkspace } from '../utils/workspaceKind'
import { resolveActiveRole } from './workspaceRoleResolution'
import { PERSONAL_SLUG_SEGMENT } from '../member/memberAppRouting'
import { createPublicWorkspaceMaskResolver } from '../services/publicWorkspaceMask'
import type { PublicWorkspaceMaskingConfig } from '../types/publicWorkspaceMask'


const DEFAULT_CHAT_CHANNEL_ID = 'workspace-chat'
const DEFAULT_CHAT_CHANNEL_SLUG = 'general'
const DEFAULT_CHAT_CHANNEL_NAME = 'General'

import type { CreateWorkspaceOptions, Workspace, WorkspaceKind, WorkspaceRole } from '../types/workspace'
export type { CreateWorkspaceOptions, Workspace, WorkspaceKind, WorkspaceRole } from '../types/workspace'

interface WorkspaceMembership {
  id?: string
  workspaceId: string
  userId?: string
  uid?: string
  user_email?: string
  email?: string
  role: WorkspaceRole
  credits?: number
  defaultDashboardView?: DashboardViewKey
  status?: 'ACTIVE' | 'PENDING' | 'DECLINED' | 'REVOKED'
}

const normalizeRole = (value: unknown): WorkspaceRole =>
  isWorkspaceRole(value) ? value : 'VIEWER'

const normalizeViewKey = (value: unknown): DashboardViewKey | null =>
  DASHBOARD_VIEW_KEYS.includes(value as DashboardViewKey) ? (value as DashboardViewKey) : null

const expandDashboardViewKeys = (keys: readonly DashboardViewKey[]): DashboardViewKey[] => {
  if (!keys.includes('HEATMAP') || keys.includes('GRID')) return [...keys]
  const expanded: DashboardViewKey[] = []
  keys.forEach((key) => {
    if (key === 'HEATMAP') expanded.push('GRID')
    expanded.push(key)
  })
  return expanded
}

const normalizeDashboardViewConfig = (
  enabledValue: unknown,
  defaultValue: unknown,
): { enabledDashboardViews: DashboardViewKey[]; defaultDashboardView: DashboardViewKey } => {
  const enabledFromData = Array.isArray(enabledValue)
    ? DASHBOARD_VIEW_KEYS.filter((key) => enabledValue.includes(key))
    : []
  // Default fallback uses AVAILABLE_VIEW_KEYS so we don't seed coming-soon views.
  const enabledDashboardViews = expandDashboardViewKeys(enabledFromData.length > 0 ? enabledFromData : AVAILABLE_VIEW_KEYS)
  const requestedDefault = normalizeViewKey(defaultValue)
  const defaultDashboardView =
    requestedDefault && enabledDashboardViews.includes(requestedDefault)
      ? requestedDefault
      : enabledDashboardViews[0]
  return { enabledDashboardViews, defaultDashboardView }
}

const resolveWorkspaceSlug = (dataSlug: unknown, workspaceId: string, workspaceName: string) => {
  if (
    typeof dataSlug === 'string' &&
    dataSlug &&
    dataSlug !== workspaceId.toLowerCase() &&
    !validateWorkspaceSlug(dataSlug)
  ) {
    return dataSlug
  }

  return suggestWorkspaceSlug(workspaceName) || 'workspace'
}

interface RawWorkspaceLimitInputs {
  workspaceLimits?: unknown
  checklistInputLimits?: unknown
}

const workspaceFromSnapshot = (
  workspaceId: string,
  data: Partial<Workspace>,
): { workspace: Workspace; rawLimits: RawWorkspaceLimitInputs } => {
  const isPersonal = isPersonalWorkspace(data as Pick<Workspace, 'isPersonal' | 'kind'>)
  const name = isPersonal ? 'Personal Space' : typeof data.name === 'string' ? data.name : 'Untitled workspace'
  const rawLimits: RawWorkspaceLimitInputs = {
    workspaceLimits: data.workspaceLimits,
    checklistInputLimits: data.checklistInputLimits,
  }
  const workspaceLimits = normalizeWorkspaceLimits(data.workspaceLimits, data.checklistInputLimits, isPersonal, data.plan)

  return {
    workspace: {
    id: workspaceId,
    name,
    slug: isPersonal ? '' : resolveWorkspaceSlug(data.slug, workspaceId, name),
    ownerId: typeof data.ownerId === 'string' ? data.ownerId : '',
    ownerIds: Array.isArray(data.ownerIds)
      ? data.ownerIds.filter((id): id is string => typeof id === 'string' && id.trim().length > 0)
      : undefined,
    // eslint-disable-next-line no-restricted-syntax -- writer normalizing the raw `kind` enum value itself (not an isPersonal predicate); isPersonalWorkspace() returns boolean and cannot replace this whitelist check.
    kind: data.kind === 'PERSONAL' || data.kind === 'TEAM' ? data.kind : undefined,
    credits: typeof data.credits === 'number' ? data.credits : undefined,
    plan: normalizeTierId(data.plan),
    subscription: {
      status: ['ACTIVE', 'PAST_DUE', 'CANCELED'].includes(String(data.subscription?.status))
        ? data.subscription!.status
        : 'ACTIVE',
      periodEnd: data.subscription?.periodEnd ?? null,
      providerSubscriptionId: typeof data.subscription?.providerSubscriptionId === 'string'
        ? data.subscription.providerSubscriptionId
        : '',
    },
    limits: {
      workspacesCount: Math.max(0, Number(data.limits?.workspacesCount) || 0),
      storageUsedBytes: Math.max(0, Number(data.limits?.storageUsedBytes) || 0),
      lastResetAt: data.limits?.lastResetAt ?? null,
    },
    timezone: typeof data.timezone === 'string' && data.timezone ? data.timezone : Intl.DateTimeFormat().resolvedOptions().timeZone,
    teamBalanceCredits: typeof data.teamBalanceCredits === 'number' ? data.teamBalanceCredits : undefined,
    inviteToken: typeof data.inviteToken === 'string' ? data.inviteToken : undefined,
    isPersonal,
    isPublic: !isPersonal && data.isPublic === true,
    workspaceLimits,
    checklistInputLimits: limitsToChecklistInputLimits(workspaceLimits.softCaps),
    actionPermissions: normalizeWorkspaceActionPermissions(data.actionPermissions),
    auditSettings: {
      retentionMode: data.auditSettings?.retentionMode === 'FULL' || data.auditSettings?.fullAuditEnabled === true ? 'FULL' : 'STANDARD',
      fullAuditEnabled: data.auditSettings?.fullAuditEnabled === true,
      lifecycleAlertEnabled: data.auditSettings?.lifecycleAlertEnabled !== false,
      enabledCategories: normalizeAuditCategories(data.auditSettings?.enabledCategories),
    },
    cardBackgroundSettings: {
      enabled: data.cardBackgroundSettings?.enabled !== false && (data as { workspacePreferences?: { cardBackgroundsEnabled?: boolean } }).workspacePreferences?.cardBackgroundsEnabled !== false,
    },
    workspacePreferences: {
      aiSuggestionsEnabled: data.workspacePreferences?.aiSuggestionsEnabled,
    },
    checklistBlockedTodoPolicy: normalizeChecklistBlockedTodoPolicy(data),
    raidBlueprint: data.raidBlueprint ? normalizeRaidBlueprint(data.raidBlueprint) : undefined,
    modules: data.modules,
    // Demo unlock (R35): carried through so plan/limit gates can read it; only the strict `true` survives.
    demoUnlock: data.demoUnlock?.enabled === true ? { enabled: true } : undefined,
    settings: {
      currency: data.settings?.currency,
      currencyLockedAt: data.settings?.currencyLockedAt ?? undefined,
      currencyLockedBy: data.settings?.currencyLockedBy ?? undefined,
      currencyMigrationDismissedAt: data.settings?.currencyMigrationDismissedAt ?? undefined,
      currencyMigrationCohortAt: data.settings?.currencyMigrationCohortAt ?? undefined,
      expenseApprovalThresholdMinor: typeof data.settings?.expenseApprovalThresholdMinor === 'number'
        ? data.settings.expenseApprovalThresholdMinor
        : null,
      quotaCoachingThresholds: Array.isArray(data.settings?.quotaCoachingThresholds)
        ? data.settings.quotaCoachingThresholds
        : undefined,
      quotaCoachingTemplates: data.settings?.quotaCoachingTemplates ?? undefined,
      quotaExtendOnResume: typeof data.settings?.quotaExtendOnResume === 'boolean'
        ? data.settings.quotaExtendOnResume
        : undefined,
    },
    onboardingSetup: data.onboardingSetup,
    deletion: data.deletion,
    ...normalizeDashboardViewConfig(data.enabledDashboardViews, data.defaultDashboardView),
  },
  rawLimits,
}
}

const createWorkspaceForUser = async (
  userId: string,
  email: string,
  displayName: string,
  name: string,
  kind: WorkspaceKind,
  options: CreateWorkspaceOptions = {},
): Promise<{ workspace: Workspace; role: WorkspaceRole }> => {
  const workspaceRef = doc(collection(firestoreDb, 'workspaces'))
  const isPersonal = kind === 'PERSONAL'
  const requestedSlug = isPersonal ? '' : options.slug?.trim() || suggestWorkspaceSlug(name)
  if (!isPersonal) {
    if (requestedSlug.toLowerCase() === PERSONAL_SLUG_SEGMENT) {
      throw new Error('The workspace URL "personal" is reserved.')
    }
    const slugError = validateWorkspaceSlug(requestedSlug)
    if (slugError) throw new Error(slugError)
  }
  const chosenCurrency = (options.currency?.trim() || detectLocaleCurrency()).toUpperCase()
  const chosenCountry = options.country?.trim().toUpperCase() || detectDeviceCountry()
  const workspaceLimits = createDefaultWorkspaceLimits(isPersonal, options.plan)
  const workspace: Workspace = {
    id: workspaceRef.id,
    name: isPersonal ? 'Personal Space' : name,
    slug: requestedSlug,
    ownerId: userId,
    ownerIds: [userId],
    kind,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    country: chosenCountry,
    credits: 0,
    plan: 'FREE_TIER',
    subscription: { status: 'ACTIVE', periodEnd: null, providerSubscriptionId: '' },
    limits: { workspacesCount: 1, storageUsedBytes: 0, lastResetAt: null },
    isPersonal,
    isPublic: kind === 'TEAM' ? options.isPublic === true : false,
    checklistInputLimits: limitsToChecklistInputLimits(workspaceLimits.softCaps),
    workspaceLimits,
    enabledDashboardViews: [...AVAILABLE_VIEW_KEYS],
    defaultDashboardView: 'TIMELINE',
    actionPermissions: { ...DEFAULT_WORKSPACE_ACTION_PERMISSIONS },
    auditSettings: { retentionMode: 'STANDARD', fullAuditEnabled: false, lifecycleAlertEnabled: true, enabledCategories: [...MINIMALIST_AUDIT_CATEGORIES] },
    cardBackgroundSettings: { enabled: true },
    checklistBlockedTodoPolicy: normalizeChecklistBlockedTodoPolicy(),
    settings: {
      currency: chosenCurrency,
      currencyLockedAt: Timestamp.now(),
      currencyLockedBy: userId,
    },
    // raidBlueprint intentionally omitted: Firestore rejects `undefined`
    // field values in setDoc/transaction.set, and the field is optional on
    // the Workspace type (populated later via the Raid blueprint panel).
  }

  if (isPersonal) {
    await setDoc(workspaceRef, {
      ...workspace,
      slug: null,
      isPublic: false,
      createdAt: serverTimestamp(),
      limits: { ...workspace.limits, lastResetAt: serverTimestamp() },
      settings: {
        ...workspace.settings,
        currency: chosenCurrency,
        currencyLockedAt: serverTimestamp(),
        currencyLockedBy: userId,
      },
    })
    return { workspace, role: 'OWNER' }
  }

  const slugRef = doc(firestoreDb, 'workspace_slugs', requestedSlug)
  const defaultChannelRef = doc(firestoreDb, 'workspaces', workspaceRef.id, 'channels', DEFAULT_CHAT_CHANNEL_ID)
  const defaultChannelSlugRef = doc(firestoreDb, 'workspaces', workspaceRef.id, 'channelSlugs', DEFAULT_CHAT_CHANNEL_SLUG)
  await runTransaction(firestoreDb, async (transaction) => {
    const slugSnapshot = await transaction.get(slugRef)
    if (slugSnapshot.exists()) throw new Error('That workspace ID is already taken.')

    transaction.set(workspaceRef, {
      ...workspace,
      reportSettings: { maskNames: true },
      auditSettings: { retentionMode: 'STANDARD', fullAuditEnabled: false, lifecycleAlertEnabled: true, enabledCategories: [...MINIMALIST_AUDIT_CATEGORIES] },
      cardBackgroundSettings: { enabled: true },
      workspacePreferences: { checklistBlockedTodoPolicy: normalizeChecklistBlockedTodoPolicy() },
      createdAt: serverTimestamp(),
      limits: { ...workspace.limits, lastResetAt: serverTimestamp() },
      settings: {
        ...workspace.settings,
        currency: chosenCurrency,
        currencyLockedAt: serverTimestamp(),
        currencyLockedBy: userId,
      },
    })
    transaction.set(slugRef, {
      slug: requestedSlug,
      workspaceId: workspaceRef.id,
      ownerId: userId,
      createdAt: serverTimestamp(),
    })
    transaction.set(defaultChannelRef, {
      name: DEFAULT_CHAT_CHANNEL_NAME,
      slug: DEFAULT_CHAT_CHANNEL_SLUG,
      type: 'public',
      roles: { [userId]: 'admin' },
      lastActivity: serverTimestamp(),
      lastReadAt: {},
      createdBy: userId,
      createdAt: serverTimestamp(),
    })
    transaction.set(defaultChannelSlugRef, {
      channelId: DEFAULT_CHAT_CHANNEL_ID,
      createdBy: userId,
      createdAt: serverTimestamp(),
    })
  })
  try {
    await createActiveOwnerMembership(firestoreDb, {
      workspaceId: workspaceRef.id,
      workspaceName: name,
      workspaceSlug: requestedSlug,
      uid: userId,
      email,
      displayName,
    })
  } catch (error) {
    console.warn('[Workspace] Created workspace, but could not create owner membership.', error)
  }

  if (workspace.isPublic && isTeamWorkspace(workspace)) {
    try {
      await setDoc(doc(firestoreDb, 'public_workspaces', requestedSlug), {
        workspaceId: workspaceRef.id,
        workspaceSlug: requestedSlug,
        name,
        ownerId: userId,
        isPersonal: false,
        isPublic: true,
        searchName: name.trim().toLowerCase(),
        searchSlug: requestedSlug,
        createdAt: serverTimestamp(),
      })
    } catch (error) {
      console.warn('[Workspace] Created public workspace, but could not publish public listing.', error)
    }
  }

  return { workspace, role: 'OWNER' }
}

export const WorkspaceProvider = ({ children }: PropsWithChildren) => {
  const planLimits = usePlanLimitOverrides()
  const [isSidebarPinned, setSidebarPinned] = usePersistedState<boolean>('rightNav.isPinned', false)
  const { currentUser, userProfile, isAuthLoading, wasLedgerDormant, getInitialWorkspace } = useAuth()
  const currentDisplayName = useDisplayName(userProfile ?? currentUser)
  const [workspaces, setWorkspaces] = useState<Workspace[]>([])
  const [rawLimitsById, setRawLimitsById] = useState<ReadonlyMap<string, RawWorkspaceLimitInputs>>(() => new Map())
  const [rolesByWorkspaceId, setRolesByWorkspaceId] = useState<Map<string, WorkspaceRole>>(new Map())
  const [creditsByWorkspaceId, setCreditsByWorkspaceId] = useState<Map<string, number>>(new Map())
  const [memberDefaultViewByWorkspaceId, setMemberDefaultViewByWorkspaceId] = useState<Map<string, DashboardViewKey>>(new Map())
  const [activeWorkspaceId, setActiveWorkspaceIdState] = useState('')
  const activeWorkspaceIdRef = useRef(activeWorkspaceId)
  const workspacesRef = useRef(workspaces)
  const rolesByWorkspaceIdRef = useRef(rolesByWorkspaceId)
  useEffect(() => {
    activeWorkspaceIdRef.current = activeWorkspaceId
  }, [activeWorkspaceId])
  useEffect(() => {
    workspacesRef.current = workspaces
  }, [workspaces])
  useEffect(() => {
    rolesByWorkspaceIdRef.current = rolesByWorkspaceId
  }, [rolesByWorkspaceId])
  const [appMode, setAppModeState] = useState<WorkspaceKind>(() =>
    window.localStorage.getItem(APP_MODE_STORAGE_KEY) === 'TEAM' ? 'TEAM' : 'PERSONAL',
  )

  const commitActiveWorkspace = useCallback((id: string, mode?: WorkspaceKind) => {
    activeWorkspaceIdRef.current = id
    setActiveWorkspaceIdState(id)
    if (typeof window !== 'undefined') {
      try {
        if (id) {
          window.sessionStorage.setItem(ACTIVE_WORKSPACE_TAB_STORAGE_KEY, id)
          window.localStorage.setItem(ACTIVE_WORKSPACE_STORAGE_KEY, id)
        } else {
          window.sessionStorage.removeItem(ACTIVE_WORKSPACE_TAB_STORAGE_KEY)
          window.localStorage.removeItem(ACTIVE_WORKSPACE_STORAGE_KEY)
        }
      } catch {
        // ignore storage errors
      }
    }
    const targetWs = workspacesRef.current.find((w) => w.id === id)
    const resolvedMode = mode ?? (targetWs ? (isPersonalWorkspace(targetWs) ? 'PERSONAL' : 'TEAM') : undefined)
    if (resolvedMode) {
      setAppModeState(resolvedMode)
      if (typeof window !== 'undefined') {
        try {
          window.localStorage.setItem(APP_MODE_STORAGE_KEY, resolvedMode)
        } catch {
          // ignore storage errors
        }
      }
    }
  }, [])

  useEffect(() => {
    return registerWorkspaceCommitter((id) => {
      commitActiveWorkspace(id ?? '')
    })
  }, [commitActiveWorkspace])

  useEffect(() => {
    return registerWorkspaceProviderBridge({
      getActiveWorkspaceId: () => activeWorkspaceIdRef.current,
      getWorkspaces: () => workspacesRef.current,
    })
  }, [])
  const [isWorkspaceLoading, setIsWorkspaceLoading] = useState(true)
  const [workspaceLoadingError, setWorkspaceLoadingError] = useState<string | null>(null)
  const activeLoadUidRef = useRef<string | null>(null)
  const activeLoadRequestIdRef = useRef<number>(0)
  const activePreviewRequestIdRef = useRef<number>(0)
  const initialLoadDoneRef = useRef(false)
  const quietReloadInFlightRef = useRef(false)
  const quietReloadQueuedRef = useRef(false)
  const liveWorkspaceIds = useMemo(
    () => workspaces.filter((workspace) => !workspace.isPreview).map((workspace) => workspace.id).sort().join('|'),
    [workspaces],
  )

  const loadWorkspacesForUser = useCallback(async (
    preferredWorkspaceId?: string,
    preferredMode?: WorkspaceKind,
    options?: { quiet?: boolean },
  ): Promise<Workspace[] | void> => {
    if (!currentUser) return []

    const targetUid = currentUser.uid
    const requestId = ++activeLoadRequestIdRef.current
    activePreviewRequestIdRef.current += 1
    activeLoadUidRef.current = targetUid

    const isCurrent = () =>
      activeLoadRequestIdRef.current === requestId && activeLoadUidRef.current === targetUid

    if (!options?.quiet) {
      setIsWorkspaceLoading(true)
    }
    setWorkspaceLoadingError(null)

    if (wasLedgerDormant && !preferredWorkspaceId) {
      clearWorkspaceStorage()
    }

    const normalizedEmail = normalizeMemberEmail(currentUser.email)
    const trackedQuery = async <T,>(label: string, promise: Promise<T>): Promise<{ ok: true; data: T } | { ok: false; error: unknown }> => {
      try {
        const data = await promise
        return { ok: true, data }
      } catch (error) {
        console.warn(`[refreshWorkspaces] query failed: ${label}`, error)
        return { ok: false, error }
      }
    }

    const isHelper = isHelperOnlyAccount({ userProfile, currentUser })
    const runQueryBundle = async () => {
      const [
        mirroredRes,
        legacyUserIdRes,
        legacyUidRes,
        legacyEmailRes,
        ownedRes,
        initialWsRes,
      ] = await Promise.all([
        trackedQuery('mirroredMemberships (users/{uid}/workspaceMemberships)', getDocs(collection(firestoreDb, 'users', currentUser.uid, 'workspaceMemberships'))),
        isHelper
          ? Promise.resolve({ ok: true as const, data: null })
          : trackedQuery('workspace_members where userId==uid', getDocs(query(collection(firestoreDb, 'workspace_members'), where('userId', '==', currentUser.uid)))),
        isHelper
          ? Promise.resolve({ ok: true as const, data: null })
          : trackedQuery('workspace_members where uid==uid', getDocs(query(collection(firestoreDb, 'workspace_members'), where('uid', '==', currentUser.uid)))),
        isHelper || !normalizedEmail
          ? Promise.resolve({ ok: true as const, data: null })
          : trackedQuery('workspace_members where email==normalizedEmail', getDocs(query(collection(firestoreDb, 'workspace_members'), where('email', '==', normalizedEmail)))),
        trackedQuery('owned workspaces where ownerId==uid', getDocs(query(collection(firestoreDb, 'workspaces'), where('ownerId', '==', currentUser.uid)))),
        trackedQuery('getInitialWorkspace', getInitialWorkspace()),
      ])
      return {
        mirroredRes,
        legacyUserIdRes,
        legacyUidRes,
        legacyEmailRes,
        ownedRes,
        initialWsRes,
      }
    }

    let queryBundle = await runQueryBundle()
    if (!isCurrent()) return

    const getFoundCount = (bundle: typeof queryBundle) => {
      const mirrored = bundle.mirroredRes.ok && bundle.mirroredRes.data ? bundle.mirroredRes.data.docs.length : 0
      const direct = [bundle.legacyUserIdRes, bundle.legacyUidRes, bundle.legacyEmailRes].reduce((sum, res) => {
        return sum + (res.ok && res.data ? res.data.docs.length : 0)
      }, 0)
      const owned = bundle.ownedRes.ok && bundle.ownedRes.data ? bundle.ownedRes.data.docs.length : 0
      return mirrored + direct + owned
    }

    const checkDiscoveryFailure = (bundle: typeof queryBundle) => {
      const allMembershipFailed =
        !bundle.mirroredRes.ok &&
        !bundle.legacyUserIdRes.ok &&
        !bundle.legacyUidRes.ok &&
        (!normalizedEmail || !bundle.legacyEmailRes.ok)
      const ownedFailed = !bundle.ownedRes.ok
      const ownedCount = bundle.ownedRes.ok && bundle.ownedRes.data ? bundle.ownedRes.data.docs.length : 0
      return ownedFailed || (allMembershipFailed && ownedCount === 0)
    }

    // First pass. If discovery did NOT fail with errors but returned 0 memberships,
    // retry once after a short delay before conceding the user has no workspaces.
    if (getFoundCount(queryBundle) === 0 && !checkDiscoveryFailure(queryBundle)) {
      await new Promise((resolve) => window.setTimeout(resolve, 800))
      if (!isCurrent()) return
      const retryBundle = await runQueryBundle()
      if (!isCurrent()) return
      if (getFoundCount(retryBundle) > 0) {
        queryBundle = retryBundle
        console.info('[refreshWorkspaces] Retry pass found workspaces after empty first pass.')
      } else {
        queryBundle = retryBundle
      }
    }

    const mirroredMemberships = queryBundle.mirroredRes.ok && queryBundle.mirroredRes.data
      ? queryBundle.mirroredRes.data.docs
          .map((snapshot) => ({ id: snapshot.id, workspaceId: snapshot.id, ...snapshot.data() }) as WorkspaceMembership)
      : []

    const legacyDocs = [
      queryBundle.legacyUserIdRes,
      queryBundle.legacyUidRes,
      queryBundle.legacyEmailRes,
    ].flatMap((res) => (res.ok && res.data ? res.data.docs : []))

    const uniqueLegacyDocs = Array.from(
      new Map(legacyDocs.map((snapshot) => [snapshot.id, snapshot])).values(),
    )
    const legacyMemberships = uniqueLegacyDocs
      .map((snapshot) => ({ id: snapshot.id, ...snapshot.data() }) as WorkspaceMembership)

    // Merge memberships by workspaceId.
    // If ANY record indicates REVOKED or DECLINED, preserve that status so unauthorized access is never granted.
    const mergedMembershipsMap = new Map<string, WorkspaceMembership>()
    for (const membership of [...legacyMemberships, ...mirroredMemberships]) {
      if (!membership.workspaceId) continue
      const existing = mergedMembershipsMap.get(membership.workspaceId)
      if (!existing) {
        mergedMembershipsMap.set(membership.workspaceId, membership)
      } else {
        const isRevokedOrDeclined =
          existing.status === 'REVOKED' || existing.status === 'DECLINED' ||
          membership.status === 'REVOKED' || membership.status === 'DECLINED'
        const resolvedStatus = isRevokedOrDeclined
          ? (existing.status === 'REVOKED' || membership.status === 'REVOKED' ? 'REVOKED' : 'DECLINED')
          : (membership.status === 'ACTIVE' || existing.status === 'ACTIVE' ? 'ACTIVE' : (membership.status ?? existing.status))
        mergedMembershipsMap.set(membership.workspaceId, {
          ...existing,
          ...membership,
          role: membership.role ?? existing.role,
          credits: typeof membership.credits === 'number' ? membership.credits : existing.credits,
          defaultDashboardView: membership.defaultDashboardView ?? existing.defaultDashboardView,
          status: resolvedStatus,
        })
      }
    }

    const memberships = Array.from(mergedMembershipsMap.values())
      .filter((membership) => membership.status === 'ACTIVE')

    let hydrationFailureCount = 0
    let attemptedHydrations = 0

    const teamWorkspaceEntries: Array<{ workspace: Workspace; rawLimits: RawWorkspaceLimitInputs } | null> = await Promise.all(
      memberships.map(async (membership) => {
        attemptedHydrations += 1
        try {
          const workspaceSnapshot = await getDoc(doc(firestoreDb, 'workspaces', membership.workspaceId))
          if (!workspaceSnapshot.exists()) return null
          const data = workspaceSnapshot.data() as Partial<Workspace>
          const entry = workspaceFromSnapshot(workspaceSnapshot.id, data)
          if (isPersonalWorkspace(entry.workspace)) return null
          if (entry.workspace.deletion?.status === 'PENDING') {
            const isOwner = isWorkspaceOwner(entry.workspace, currentUser?.uid)
            if (!isOwner) return null
          }
          return entry
        } catch (error) {
          hydrationFailureCount += 1
          console.warn(`[refreshWorkspaces] Failed to hydrate workspace ${membership.workspaceId}`, error)
          return null
        }
      }),
    )
    if (!isCurrent()) return

    const ownedWorkspaceEntries = (queryBundle.ownedRes.ok && queryBundle.ownedRes.data ? queryBundle.ownedRes.data.docs : [])
      .map((workspaceSnapshot) => workspaceFromSnapshot(workspaceSnapshot.id, workspaceSnapshot.data() as Partial<Workspace>))
    const ownedWorkspaceDocs = ownedWorkspaceEntries.map((entry) => entry.workspace)
    const teamWorkspaceDocs = teamWorkspaceEntries
      .filter((entry): entry is { workspace: Workspace; rawLimits: RawWorkspaceLimitInputs } => entry !== null)
      .map((entry) => entry.workspace)
    const ownedTeamWorkspaceDocs = ownedWorkspaceDocs.filter((workspace) => isTeamWorkspace(workspace))
    const personalWorkspaceDocs = ownedWorkspaceDocs
      .filter((workspace) => isPersonalWorkspace(workspace))

    const loadedRawLimits = new Map<string, RawWorkspaceLimitInputs>()
    for (const entry of teamWorkspaceEntries) {
      if (entry) loadedRawLimits.set(entry.workspace.id, entry.rawLimits)
    }
    for (const entry of ownedWorkspaceEntries) {
      loadedRawLimits.set(entry.workspace.id, entry.rawLimits)
    }

    const realWorkspaces = Array.from(
      new Map([
        ...ownedTeamWorkspaceDocs,
        ...teamWorkspaceDocs.filter((workspace): workspace is Workspace => workspace !== null),
        ...personalWorkspaceDocs,
      ].map((workspace) => [workspace.id, workspace])).values(),
    ).filter((workspace) => {
      if (workspace.deletion?.status === 'PENDING') {
        return isWorkspaceOwner(workspace, currentUser?.uid)
      }
      return true
    })
    const previewWorkspaces = readRecentExplorePreviewSeeds().map((seed) => ({
      ...toPreviewWorkspace(seed),
      previewExpiresAt: Date.now() + EXPLORE_PREVIEW_TTL_MS,
    }))
    const nextWorkspaces = Array.from(
      new Map([...realWorkspaces, ...previewWorkspaces].map((workspace) => [workspace.id, workspace])).values(),
    )
    const teamWorkspaceIds = new Set(realWorkspaces.filter((workspace) => isTeamWorkspace(workspace)).map((workspace) => workspace.id))
    const nextRoles = new Map<string, WorkspaceRole>([
      ...memberships
        .filter((membership) => teamWorkspaceIds.has(membership.workspaceId))
        .map((membership) => [membership.workspaceId, normalizeRole(membership.role)] as const),
      ...ownedTeamWorkspaceDocs.map((workspace) => [workspace.id, 'OWNER' as WorkspaceRole] as const),
      ...personalWorkspaceDocs.map((workspace) => [workspace.id, 'OWNER' as WorkspaceRole] as const),
      ...previewWorkspaces.map((workspace) => [workspace.id, 'READONLY' as WorkspaceRole] as const),
    ])
    const nextCredits = new Map(
      memberships
        .filter((membership) => teamWorkspaceIds.has(membership.workspaceId))
        .map((membership) => [
          membership.workspaceId,
          typeof membership.credits === 'number' ? membership.credits : 0,
        ] as const),
    )
    const nextMemberDefaultViews = new Map(
      memberships
        .filter((membership) => teamWorkspaceIds.has(membership.workspaceId))
        .map((membership) => [membership.workspaceId, normalizeViewKey(membership.defaultDashboardView)] as const)
        .filter((entry): entry is readonly [string, DashboardViewKey] => entry[1] !== null),
    )
    const initialWorkspace = queryBundle.initialWsRes.ok ? queryBundle.initialWsRes.data : null
    let urlWorkspaceId: string | null = null
    if (typeof window !== 'undefined') {
      const parts = getConsoleParts(window.location.pathname)
      if (parts[0] === 'workspace' && parts[1]) {
        const slugOrId = parts[1]
        const matched = nextWorkspaces.find((w) => !w.isPreview && (w.slug === slugOrId || w.id === slugOrId))
        if (matched) urlWorkspaceId = matched.id
      }
    }
    let tabWorkspaceId: string | null = null
    let deviceWorkspaceId: string | null = null
    if (typeof window !== 'undefined') {
      try {
        tabWorkspaceId = window.sessionStorage.getItem(ACTIVE_WORKSPACE_TAB_STORAGE_KEY)
      } catch {
        // ignore
      }
      try {
        deviceWorkspaceId = window.localStorage.getItem(ACTIVE_WORKSPACE_STORAGE_KEY)
      } catch {
        // ignore
      }
    }
    const effectivePreferredWorkspaceId = preferredWorkspaceId ?? (options?.quiet && activeWorkspaceIdRef.current ? activeWorkspaceIdRef.current : undefined)
    const savedWorkspaceId =
      effectivePreferredWorkspaceId ??
      urlWorkspaceId ??
      tabWorkspaceId ??
      deviceWorkspaceId ??
      initialWorkspace?.workspaceId ??
      null
    const targetDirectWorkspace = urlWorkspaceId ? nextWorkspaces.find((w) => w.id === urlWorkspaceId) : null
    const nextMode = preferredMode ?? (targetDirectWorkspace ? (isPersonalWorkspace(targetDirectWorkspace) ? 'PERSONAL' : 'TEAM') : appMode)
    const modeWorkspaces = nextWorkspaces.filter((workspace) =>
      nextMode === 'PERSONAL' ? isPersonalWorkspace(workspace) : isTeamWorkspace(workspace),
    )
    const fallbackMode: WorkspaceKind = nextMode === 'PERSONAL' ? 'TEAM' : 'PERSONAL'
    const fallbackWorkspaces = nextWorkspaces.filter((workspace) =>
      fallbackMode === 'PERSONAL' ? isPersonalWorkspace(workspace) : isTeamWorkspace(workspace),
    )
    // Only respect a saved workspace id if it's a REAL workspace. A saved
    // preview id from a previous exploration must not become the landing page —
    // users expect to land on their last real workspace, not the demo.
    const isRealSaved = savedWorkspaceId
      && nextWorkspaces.some((workspace) => workspace.id === savedWorkspaceId && !workspace.isPreview)
    // First real workspace in the preferred mode; falls back to any real workspace.
    const firstRealInMode = modeWorkspaces.find((workspace) => !workspace.isPreview)
    const firstRealAnywhere = fallbackWorkspaces.find((workspace) => !workspace.isPreview)
      ?? nextWorkspaces.find((workspace) => !workspace.isPreview)
    const nextActiveWorkspaceId =
      isRealSaved
        ? savedWorkspaceId!
        : firstRealInMode?.id ?? firstRealAnywhere?.id ?? modeWorkspaces[0]?.id ?? fallbackWorkspaces[0]?.id ?? nextWorkspaces[0]?.id ?? ''
    const activeWorkspace = nextWorkspaces.find((workspace) => workspace.id === nextActiveWorkspaceId)
    const resolvedMode: WorkspaceKind = isPersonalWorkspace(activeWorkspace) ? 'PERSONAL' : 'TEAM'

    const finalDiscoveryFailure = checkDiscoveryFailure(queryBundle)

    let computedLoadingError: string | null = null
    if (realWorkspaces.length === 0 && (finalDiscoveryFailure || (attemptedHydrations > 0 && hydrationFailureCount === attemptedHydrations))) {
      computedLoadingError = 'Failed to load workspaces. Please check your connection and retry.'
    } else if (hydrationFailureCount > 0) {
      computedLoadingError = 'Some workspaces could not be loaded.'
    }

    if (!isCurrent()) return []

    setWorkspaces(nextWorkspaces)
    setRawLimitsById(loadedRawLimits)
    setRolesByWorkspaceId(nextRoles)
    setCreditsByWorkspaceId(nextCredits)
    setMemberDefaultViewByWorkspaceId(nextMemberDefaultViews)
    commitActiveWorkspace(nextActiveWorkspaceId, resolvedMode)
    setWorkspaceLoadingError(computedLoadingError)
    initialLoadDoneRef.current = true
    setIsWorkspaceLoading(false)
    return nextWorkspaces
  }, [appMode, commitActiveWorkspace, currentUser, getInitialWorkspace, userProfile?.role, wasLedgerDormant])

  const loadWorkspacesForUserRef = useRef(loadWorkspacesForUser)
  useEffect(() => {
    loadWorkspacesForUserRef.current = loadWorkspacesForUser
  }, [loadWorkspacesForUser])

  const triggerQuietWorkspacesReload = useCallback(async () => {
    if (quietReloadInFlightRef.current) {
      quietReloadQueuedRef.current = true
      return
    }
    quietReloadInFlightRef.current = true
    try {
      do {
        quietReloadQueuedRef.current = false
        await loadWorkspacesForUserRef.current(undefined, undefined, { quiet: true })
      } while (quietReloadQueuedRef.current)
    } finally {
      quietReloadInFlightRef.current = false
    }
  }, [])

  useEffect(() => {
    if (isAuthLoading) return

    if (!currentUser) {
      activeLoadUidRef.current = null
      activeLoadRequestIdRef.current += 1
      initialLoadDoneRef.current = false
      quietReloadInFlightRef.current = false
      quietReloadQueuedRef.current = false
      const timeout = window.setTimeout(() => {
        setRawLimitsById(new Map())
        setWorkspaces([])
        setRolesByWorkspaceId(new Map())
        setCreditsByWorkspaceId(new Map())
        setMemberDefaultViewByWorkspaceId(new Map())
        commitActiveWorkspace('')
        setWorkspaceLoadingError(null)
        setIsWorkspaceLoading(false)
      }, 0)
      return () => window.clearTimeout(timeout)
    }

    let isCancelled = false
    const loadTimeout = window.setTimeout(() => {
      void loadWorkspacesForUserRef.current().catch((err) => {
        if (isCancelled || !activeLoadUidRef.current) return
        console.warn('[WorkspaceContext] loadWorkspacesForUser rejected', err)
        setWorkspaceLoadingError(err instanceof Error ? err.message : 'Failed to load workspaces.')
        setIsWorkspaceLoading(false)
      })
    }, 0)

    return () => {
      isCancelled = true
      activeLoadRequestIdRef.current += 1
      window.clearTimeout(loadTimeout)
    }
  }, [commitActiveWorkspace, currentUser, isAuthLoading])

  const setActiveWorkspaceId = useCallback((workspaceId: string) => {
    commitActiveWorkspace(workspaceId)
  }, [commitActiveWorkspace])

  // Aria & Co. demo is NOT auto-mounted anymore. It only appears in the
  // workspace switcher once the user has opened it via the Explore hub
  // (openPreviewWorkspace persists it via rememberExplorePreviewSeed, and
  // subsequent workspace loads re-hydrate remembered seeds via
  // readRecentExplorePreviewSeeds).

  const openPreviewWorkspace = useCallback((workspace: Workspace) => {
    if (!workspace.isPreview) return
    if (workspace.previewSource === 'EXPLORE_SEED') rememberExplorePreviewSeed(workspace.id)
    try {
      const raw = window.sessionStorage.getItem('timelining-preview-open-count')
      const next = (raw ? Number(raw) : 0) + 1
      window.sessionStorage.setItem('timelining-preview-open-count', String(next))
      window.dispatchEvent(new CustomEvent('timelining:preview-opened', { detail: { workspaceId: workspace.id, kind: workspace.previewKind, totalOpens: next } }))
    } catch { /* ignore telemetry write failures */ }
    const previewWorkspace = {
      ...workspace,
      previewExpiresAt: Date.now() + EXPLORE_PREVIEW_TTL_MS,
    }
    setWorkspaces((current) => {
      const withoutExisting = current.filter((entry) => entry.id !== previewWorkspace.id)
      return [...withoutExisting, previewWorkspace]
    })
    setRolesByWorkspaceId((current) => {
      const next = new Map(current)
      next.set(previewWorkspace.id, 'READONLY')
      return next
    })
    setCreditsByWorkspaceId((current) => {
      const next = new Map(current)
      next.set(previewWorkspace.id, 0)
      return next
    })
    setMemberDefaultViewByWorkspaceId((current) => {
      const next = new Map(current)
      next.set(previewWorkspace.id, previewWorkspace.defaultDashboardView)
      return next
    })
    commitActiveWorkspace(previewWorkspace.id, 'TEAM')
  }, [commitActiveWorkspace])

  const setAppMode = useCallback((mode: WorkspaceKind) => {
    setAppModeState(mode)
    window.localStorage.setItem(APP_MODE_STORAGE_KEY, mode)
  }, [])

  const openPublicWorkspacePreview = useCallback(async ({ workspaceId, slug }: { workspaceId: string; slug: string }) => {
    // A slower earlier preview never replaces a workspace the user is a real member of.
    // Open the member workspace normally instead.
    const existingMemberWorkspace = workspacesRef.current.find(
      (w) => (w.id === workspaceId || (slug && w.slug === slug)) && !w.isPreview,
    )
    if (existingMemberWorkspace) {
      void requestWorkspaceSwitch({
        toWorkspaceId: existingMemberWorkspace.id,
        source: 'USER',
        landing: safeWorkspaceLanding(existingMemberWorkspace, 'DASHBOARD'),
      })
      return
    }

    const previewRequestId = ++activePreviewRequestIdRef.current
    const loadRequestIdAtStart = activeLoadRequestIdRef.current

    // Read the public mirror doc first (this is what an unauthenticated Admin
    // control writes). If missing, fall through to reading the real workspace
    // doc, which any signed-in user has permission to read via the existing
    // rules for public workspaces.
    const mirrorRef = doc(firestoreDb, 'public_workspaces', slug || workspaceId)
    const [mirrorSnapshot, workspaceSnapshot] = await Promise.all([
      getDoc(mirrorRef).catch(() => null),
      getDoc(doc(firestoreDb, 'workspaces', workspaceId)).catch(() => null),
    ])

    // Race check: slower earlier preview never replaces a later preview or a later real load
    if (
      activePreviewRequestIdRef.current !== previewRequestId ||
      activeLoadRequestIdRef.current !== loadRequestIdAtStart
    ) {
      return
    }

    // Secondary membership check if a real load completed during fetch
    const postFetchMember = workspacesRef.current.find(
      (w) => (w.id === workspaceId || (slug && w.slug === slug)) && !w.isPreview,
    )
    if (postFetchMember) {
      void requestWorkspaceSwitch({
        toWorkspaceId: postFetchMember.id,
        source: 'USER',
        landing: safeWorkspaceLanding(postFetchMember, 'DASHBOARD'),
      })
      return
    }
    const mirror = mirrorSnapshot?.exists() ? mirrorSnapshot.data() as Record<string, unknown> : null
    const workspaceData = workspaceSnapshot?.exists() ? workspaceSnapshot.data() as Partial<Workspace> : null
    const mask = normalizeWorkspaceSurfaceMask(mirror?.surfaceMask ?? workspaceData?.previewSurfaceMask)
      ?? { surfaces: { OVERVIEW: true, DASHBOARD: true }, sensitive: {} }
    const workspaceLimits = createDefaultWorkspaceLimits(false, workspaceData?.plan)
    const actionPermissions = normalizeWorkspaceActionPermissions(workspaceData?.actionPermissions)
    const maskingConfig = (mirror?.maskingConfig ?? workspaceData?.maskingConfig) as PublicWorkspaceMaskingConfig | undefined
    const maskResolver = maskingConfig?.enabled
      ? createPublicWorkspaceMaskResolver(workspaceId, maskingConfig)
      : null
    const maskedOwner = maskResolver
      ? maskResolver.resolveMemberAlias(workspaceData?.ownerId || 'owner')
      : null

    const previewWorkspace: Workspace = {
      id: workspaceId,
      name: typeof mirror?.name === 'string' ? mirror.name as string
        : typeof workspaceData?.name === 'string' ? workspaceData.name : 'Public workspace',
      slug: slug || workspaceId,
      ownerId: maskedOwner ? `masked_${maskedOwner.initials}` : typeof workspaceData?.ownerId === 'string' ? workspaceData.ownerId : '',
      kind: 'TEAM',
      credits: 0,
      plan: (workspaceData?.plan as Workspace['plan']) ?? 'PROFESSIONAL_TIER',
      subscription: { status: 'ACTIVE', periodEnd: null, providerSubscriptionId: '' },
      limits: { workspacesCount: 1, storageUsedBytes: 0, lastResetAt: null },
      timezone: workspaceData?.timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone,
      teamBalanceCredits: 0,
      isPersonal: false,
      isPublic: true,
      publicListedInExplore: (mirror?.listedInExplore as boolean | undefined) === true,
      checklistInputLimits: limitsToChecklistInputLimits(workspaceLimits.softCaps),
      workspaceLimits,
      enabledDashboardViews: workspaceData?.enabledDashboardViews ?? [...AVAILABLE_VIEW_KEYS],
      defaultDashboardView: workspaceData?.defaultDashboardView ?? 'TIMELINE',
      actionPermissions,
      auditSettings: {
        retentionMode: 'STANDARD',
        fullAuditEnabled: false,
        lifecycleAlertEnabled: true,
        enabledCategories: ['CONTENT_CHANGES', 'ROLE_CHANGES', 'BILLING'],
      },
      cardBackgroundSettings: { enabled: true },
      checklistBlockedTodoPolicy: normalizeChecklistBlockedTodoPolicy(workspaceData),
      modules: workspaceData?.modules,
      isPreview: true,
      previewKind: 'PUBLIC',
      previewSource: 'PUBLIC_WORKSPACE',
      previewExpiresAt: Date.now() + EXPLORE_PREVIEW_TTL_MS,
      previewSurfaceMask: mask,
      maskingConfig,
      previewCover: {
        tagline: typeof (mirror?.cover as Record<string, unknown> | undefined)?.tagline === 'string'
          ? (mirror!.cover as Record<string, string>).tagline
          : undefined,
        category: typeof (mirror?.cover as Record<string, unknown> | undefined)?.category === 'string'
          ? (mirror!.cover as Record<string, string>).category
          : undefined,
        aboutMarkdown: typeof mirror?.about === 'string' ? mirror.about as string : undefined,
      },
    }
    setWorkspaces((current) => {
      const withoutExisting = current.filter((entry) => entry.id !== previewWorkspace.id)
      return [...withoutExisting, previewWorkspace]
    })
    setRolesByWorkspaceId((current) => {
      const next = new Map(current)
      next.set(previewWorkspace.id, 'READONLY')
      return next
    })
    setCreditsByWorkspaceId((current) => {
      const next = new Map(current)
      next.set(previewWorkspace.id, 0)
      return next
    })
    commitActiveWorkspace(previewWorkspace.id, 'TEAM')
    try {
      const raw = window.sessionStorage.getItem('timelining-preview-open-count')
      const next = (raw ? Number(raw) : 0) + 1
      window.sessionStorage.setItem('timelining-preview-open-count', String(next))
      window.dispatchEvent(new CustomEvent('timelining:preview-opened', { detail: { workspaceId: previewWorkspace.id, kind: 'PUBLIC', totalOpens: next } }))
    } catch { /* ignore telemetry write failures */ }
  }, [commitActiveWorkspace])

  const checkWorkspaceSlugAvailability = useCallback(async (slug: string) => {
    const nextSlug = slug.trim()
    const slugError = validateWorkspaceSlug(nextSlug)
    if (slugError) return { available: false, error: slugError }
    const snapshot = await getDoc(doc(firestoreDb, 'workspace_slugs', nextSlug))
    return snapshot.exists()
      ? { available: false, error: 'That workspace ID is already taken.' }
      : { available: true, error: null }
  }, [])

  const createWorkspace = useCallback(async (
    name: string,
    kind: WorkspaceKind,
    options: boolean | CreateWorkspaceOptions = {},
  ) => {
    if (!currentUser) throw new Error('You must be signed in to create a workspace.')

    const resolvedOptions = typeof options === 'boolean' ? { isPublic: options } : options
    const isPersonalRequest = kind === 'PERSONAL'
    const nextName = isPersonalRequest ? 'Personal Space' : name.trim() || 'Team Workspace'
    const ownedWorkspaces = workspaces.filter((workspace) => !workspace.isPreview && workspace.ownerId === currentUser.uid)
    if (isPersonalRequest && ownedWorkspaces.some((workspace) => isPersonalWorkspace(workspace))) {
      throw new Error('Your account can have only one Personal Space.')
    }
    if (!isPersonalRequest && ownedWorkspaces.some((workspace) => isTeamWorkspace(workspace))) {
      throw new Error('Your account can create only one Team workspace.')
    }
    const { workspace, role } = await createWorkspaceForUser(
      currentUser.uid,
      currentUser.email ?? '',
      currentDisplayName || currentUser.email || '',
      nextName,
      kind,
      resolvedOptions,
    )

    setRawLimitsById((prev) => new Map(prev).set(workspace.id, {
      workspaceLimits: workspace.workspaceLimits,
      checklistInputLimits: workspace.checklistInputLimits,
    }))
    setWorkspaces((current) => [...current, workspace])
    setRolesByWorkspaceId((current) => {
      const next = new Map(current)
      next.set(workspace.id, role)
      return next
    })
    commitActiveWorkspace(workspace.id, kind)
    setCreditsByWorkspaceId((current) => {
      const next = new Map(current)
      if (isTeamWorkspace(workspace)) next.set(workspace.id, 0)
      return next
    })

    return workspace
  }, [commitActiveWorkspace, currentDisplayName, currentUser, workspaces])

  const removeWorkspaceFromState = useCallback((workspaceId: string, reason: 'removed' | 'deleted' = 'removed') => {
    setRawLimitsById((prev) => {
      const next = new Map(prev)
      next.delete(workspaceId)
      return next
    })
    setRolesByWorkspaceId((current) => {
      const next = new Map(current)
      next.delete(workspaceId)
      rolesByWorkspaceIdRef.current = next
      return next
    })

    setCreditsByWorkspaceId((current) => {
      const next = new Map(current)
      next.delete(workspaceId)
      return next
    })

    setMemberDefaultViewByWorkspaceId((current) => {
      if (!current.has(workspaceId)) return current
      const next = new Map(current)
      next.delete(workspaceId)
      return next
    })

    const lostWorkspace = workspacesRef.current.find((workspace) => workspace.id === workspaceId)
    const nextWorkspaces = workspacesRef.current.filter((workspace) => workspace.id !== workspaceId)
    workspacesRef.current = nextWorkspaces
    setWorkspaces(nextWorkspaces)

    void markHelperOutboxWorkspaceAccessLost(workspaceId, lostWorkspace?.name)

    const isActiveLost =
      Boolean(activeWorkspaceIdRef.current) &&
      (activeWorkspaceIdRef.current === workspaceId ||
        !nextWorkspaces.some((w) => w.id === activeWorkspaceIdRef.current))

    if (isActiveLost) {
      const allowedNonPreview = nextWorkspaces.filter((workspace) => !workspace.isPreview)
      const lostKind: WorkspaceKind = isPersonalWorkspace(lostWorkspace) ? 'PERSONAL' : 'TEAM'

      const recentIds = getRecentWorkspaceIds()

      const recentSameKindId = recentIds.find((id) =>
        allowedNonPreview.some((w) => w.id === id && (isPersonalWorkspace(w) ? 'PERSONAL' : 'TEAM') === lostKind),
      )
      const sameKindFallback = allowedNonPreview.find(
        (w) => (isPersonalWorkspace(w) ? 'PERSONAL' : 'TEAM') === lostKind,
      )
      const recentAnyKindId = recentIds.find((id) =>
        allowedNonPreview.some((w) => w.id === id),
      )
      const anyKindFallback = allowedNonPreview[0]?.id

      const nextActiveWorkspaceId = recentSameKindId ?? sameKindFallback?.id ?? recentAnyKindId ?? anyKindFallback ?? null

      const nextTargetWs = nextWorkspaces.find((w) => w.id === nextActiveWorkspaceId)
      const landing = nextTargetWs ? safeWorkspaceLanding(nextTargetWs, 'DASHBOARD') : undefined

      const existingReq = getAccessLostRequest()
      promptWorkspaceAccessLost({
        workspaceName: existingReq?.workspaceName || lostWorkspace?.name || 'workspace',
        reason: existingReq?.reason || reason,
        toWorkspaceId: nextActiveWorkspaceId,
        landing,
      })
    }
  }, [])

  useEffect(() => {
    if (!currentUser?.uid) return

    const unsubscribe = subscribeToMyMemberships(currentUser.uid, {
      onChange: (change) => {
        const workspaceId = change.workspaceId
        if (change.type === 'removed') {
          removeWorkspaceFromState(workspaceId, 'removed')
        } else if (change.type === 'modified') {
          if (change.status && change.status !== 'ACTIVE') {
            removeWorkspaceFromState(workspaceId, 'removed')
          } else if (change.role) {
            const newRole = normalizeRole(change.role)
            const oldRole = rolesByWorkspaceIdRef.current.get(workspaceId)
            if (newRole && newRole !== oldRole) {
              setRolesByWorkspaceId((prev) => new Map(prev).set(workspaceId, newRole))
              const ws = workspacesRef.current.find((w) => w.id === workspaceId)
              const wsName = ws?.name || 'workspace'
              const roleLabel = formatRoleLabel(getNlsString, newRole)
              emitToast(getNlsString('NLSRoleChangedToast', { name: wsName, role: roleLabel }), 'info')

              if (activeWorkspaceIdRef.current === workspaceId && ws) {
                const wrapper = getWrapperRole()
                const app: SwitchLandingApp = wrapper === 'helper' ? 'HELPER' : wrapper === 'member' ? 'MEMBER' : 'WEB'
                const current = typeof window !== 'undefined' ? window.location.pathname + window.location.search : ''
                const landing = resolveSwitchLanding({
                  app,
                  current,
                  target: ws,
                  role: newRole,
                })
                if (landing && typeof window !== 'undefined' && window.location.pathname !== landing) {
                  window.history.pushState(null, '', landing)
                  window.dispatchEvent(new PopStateEvent('popstate'))
                }
              }
            }
          }
        } else if (change.type === 'added') {
          if (change.isInitial || !initialLoadDoneRef.current) {
            return
          }
          if ((!change.status || change.status === 'ACTIVE') && !workspacesRef.current.some((w) => w.id === workspaceId)) {
            void triggerQuietWorkspacesReload()
          }
        }
      },
      onError: (error) => {
        console.warn('[WorkspaceContext] Live users/{uid}/workspaceMemberships listener failed:', error)
      },
    })

    return () => {
      unsubscribe()
    }
  }, [currentUser?.uid, triggerQuietWorkspacesReload, removeWorkspaceFromState])

  useEffect(() => {
    if (!currentUser || !liveWorkspaceIds) return

    const unsubscribeCallbacks = liveWorkspaceIds.split('|').map((workspaceId) =>
      onSnapshot(
        doc(firestoreDb, 'workspaces', workspaceId),
        (snapshot) => {
          if (!snapshot.exists()) {
            removeWorkspaceFromState(workspaceId, 'deleted')
            return
          }

          const data = snapshot.data() as Partial<Workspace>
          const isPersonal = isPersonalWorkspace(data as Pick<Workspace, 'isPersonal' | 'kind'>)
          const kind: WorkspaceKind = isPersonal ? 'PERSONAL' : 'TEAM'
          setRawLimitsById((prev) => new Map(prev).set(workspaceId, {
            workspaceLimits: data.workspaceLimits,
            checklistInputLimits: data.checklistInputLimits,
          }))
          setWorkspaces((current) =>
            current.map((workspace) => {
              if (workspace.id !== workspaceId) return workspace

              const workspaceLimits = normalizeWorkspaceLimits(data.workspaceLimits, data.checklistInputLimits, isPersonal, data.plan)
              return {
                ...workspace,
                name: typeof data.name === 'string' ? data.name : workspace.name,
                slug: resolveWorkspaceSlug(data.slug, workspaceId, typeof data.name === 'string' ? data.name : workspace.name),
                ownerId: typeof data.ownerId === 'string' ? data.ownerId : workspace.ownerId,
                ownerIds: Array.isArray(data.ownerIds)
                  ? data.ownerIds.filter((id): id is string => typeof id === 'string' && id.trim().length > 0)
                  : workspace.ownerIds,
                kind,
                credits: typeof data.credits === 'number' ? data.credits : workspace.credits,
                plan: typeof data.plan === 'string' ? data.plan : workspace.plan,
                teamBalanceCredits: typeof data.teamBalanceCredits === 'number'
                  ? data.teamBalanceCredits
                  : workspace.teamBalanceCredits,
                inviteToken: typeof data.inviteToken === 'string' ? data.inviteToken : workspace.inviteToken,
                isPersonal,
                isPublic: data.isPublic === true,
                publicListedInExplore: (data as { publicListedInExplore?: boolean }).publicListedInExplore === true,
                previewSurfaceMask: normalizeWorkspaceSurfaceMask((data as { previewSurfaceMask?: unknown }).previewSurfaceMask) ?? workspace.previewSurfaceMask,
                previewCover: (data as { previewCover?: Workspace['previewCover'] }).previewCover ?? workspace.previewCover,
                workspaceLimits,
                checklistInputLimits: limitsToChecklistInputLimits(workspaceLimits.softCaps),
                actionPermissions: normalizeWorkspaceActionPermissions(data.actionPermissions),
                auditSettings: {
                  retentionMode: data.auditSettings?.retentionMode === 'FULL' || data.auditSettings?.fullAuditEnabled === true ? 'FULL' : 'STANDARD',
                  fullAuditEnabled: data.auditSettings?.fullAuditEnabled === true,
                  lifecycleAlertEnabled: data.auditSettings?.lifecycleAlertEnabled !== false,
                  enabledCategories: normalizeAuditCategories(data.auditSettings?.enabledCategories),
                },
                cardBackgroundSettings: {
                  enabled: data.cardBackgroundSettings?.enabled !== false && (data as { workspacePreferences?: { cardBackgroundsEnabled?: boolean } }).workspacePreferences?.cardBackgroundsEnabled !== false,
                },
                workspacePreferences: {
                  aiSuggestionsEnabled: data.workspacePreferences?.aiSuggestionsEnabled !== undefined
                    ? data.workspacePreferences.aiSuggestionsEnabled
                    : workspace.workspacePreferences?.aiSuggestionsEnabled,
                },
                checklistBlockedTodoPolicy: normalizeChecklistBlockedTodoPolicy(data),
                raidBlueprint: data.raidBlueprint ? normalizeRaidBlueprint(data.raidBlueprint) : undefined,
                modules: data.modules ?? workspace.modules,
                settings: {
                  currency: data.settings?.currency ?? workspace.settings?.currency,
                  expenseApprovalThresholdMinor: data.settings?.expenseApprovalThresholdMinor !== undefined
                    ? data.settings.expenseApprovalThresholdMinor
                    : workspace.settings?.expenseApprovalThresholdMinor,
                  quotaCoachingThresholds: data.settings?.quotaCoachingThresholds !== undefined
                    ? data.settings.quotaCoachingThresholds
                    : workspace.settings?.quotaCoachingThresholds,
                  quotaCoachingTemplates: data.settings?.quotaCoachingTemplates !== undefined
                    ? data.settings.quotaCoachingTemplates
                    : workspace.settings?.quotaCoachingTemplates,
                  quotaExtendOnResume: data.settings?.quotaExtendOnResume !== undefined
                    ? data.settings.quotaExtendOnResume
                    : workspace.settings?.quotaExtendOnResume,
                },
                onboardingSetup: data.onboardingSetup ?? workspace.onboardingSetup,
                ...normalizeDashboardViewConfig(data.enabledDashboardViews, data.defaultDashboardView),
              }
            }),
          )
        },
        (error) => {
          const isPermDenied = (error as { code?: string })?.code === 'permission-denied' ||
            (error as Error)?.message?.includes('permission-denied')
          if (isPermDenied && activeWorkspaceIdRef.current === workspaceId) {
            removeWorkspaceFromState(workspaceId, 'removed')
            return
          }
          console.warn('[Firestore listener failed] workspaces live refresh', workspaceId, error)
        },
      ),
    )

    return () => unsubscribeCallbacks.forEach((unsubscribe) => unsubscribe())
  }, [currentUser, liveWorkspaceIds, removeWorkspaceFromState])

  const normalizedWorkspaces = useMemo(() => {
    return workspaces.map((workspace) => {
      const raw = rawLimitsById.get(workspace.id)
      const rawWorkspaceLimits = raw ? raw.workspaceLimits : workspace.workspaceLimits
      const rawChecklistInputLimits = raw ? raw.checklistInputLimits : workspace.checklistInputLimits
      const isPersonal = isPersonalWorkspace(workspace)
      const workspaceLimits = normalizeWorkspaceLimits(
        rawWorkspaceLimits,
        rawChecklistInputLimits,
        isPersonal,
        workspace.plan,
        planLimits,
      )
      return {
        ...workspace,
        workspaceLimits,
        checklistInputLimits: limitsToChecklistInputLimits(workspaceLimits.softCaps),
      }
    })
  }, [workspaces, rawLimitsById, planLimits])

  // Count only REAL workspaces. Preview workspaces (Explore seeds, demo mounts)
  // can persist in localStorage across Firebase projects sharing an origin
  // (e.g. localhost:5173 used for both DEV and SIT), so a user with zero real
  // workspaces but leftover preview entries must still see onboarding.
  const realWorkspaceCount = normalizedWorkspaces.filter((workspace) => !workspace.isPreview).length
  const hasFatalLoadError = Boolean(workspaceLoadingError && realWorkspaceCount === 0)
  const needsOnboarding = Boolean(currentUser && !isWorkspaceLoading && realWorkspaceCount === 0 && !hasFatalLoadError)
  const currentUserCredits = creditsByWorkspaceId.get(activeWorkspaceId) ?? 0
  const activeMemberDefaultView = memberDefaultViewByWorkspaceId.get(activeWorkspaceId) ?? null

  const setMemberDefaultDashboardView = useCallback(async (workspaceId: string, view: DashboardViewKey) => {
    const target = normalizedWorkspaces.find((workspace) => workspace.id === workspaceId)
    if (target?.isPreview) throw new Error('Preview workspaces are read-only.')
    if (isPersonalWorkspace(target)) {
      await updateDoc(doc(firestoreDb, 'workspaces', workspaceId), { defaultDashboardView: view })
      return
    }
    const email = normalizeMemberEmail(currentUser?.email)
    if (!email) throw new Error('You must be signed in to set a default view.')

    await updateDoc(getMembershipRef(firestoreDb, workspaceId, email), { defaultDashboardView: view })
    setMemberDefaultViewByWorkspaceId((current) => {
      const next = new Map(current)
      next.set(workspaceId, view)
      return next
    })
  }, [currentUser?.email, normalizedWorkspaces])

  const setWorkspaceDashboardViewConfig = useCallback(async (
    workspaceId: string,
    patch: { enabledViews?: DashboardViewKey[]; defaultView?: DashboardViewKey },
  ) => {
    const target = normalizedWorkspaces.find((workspace) => workspace.id === workspaceId)
    if (!target) throw new Error('Workspace not found.')
    if (target.isPreview) throw new Error('Preview workspaces are read-only.')

    const { enabledDashboardViews, defaultDashboardView } = normalizeDashboardViewConfig(
      patch.enabledViews ?? target.enabledDashboardViews,
      patch.defaultView ?? target.defaultDashboardView,
    )
    await updateDoc(doc(firestoreDb, 'workspaces', workspaceId), {
      enabledDashboardViews,
      defaultDashboardView,
    })
  }, [normalizedWorkspaces])

  const setWorkspaceChecklistLimit = useCallback(async (
    workspaceId: string,
    field: keyof ChecklistInputLimits,
    value: number,
  ): Promise<number> => {
    const target = normalizedWorkspaces.find((workspace) => workspace.id === workspaceId)
    if (!target) throw new Error('Workspace not found.')
    if (target.isPreview) throw new Error('Preview workspaces are read-only.')
    if (isPersonalWorkspace(target)) throw new Error('Personal workspace checklist limits are fixed and cannot be changed.')

    const applied = clampIntegerToLimit(value, CHECKLIST_INPUT_HARD_LIMITS[field], target.checklistInputLimits[field])
    const checklistInputLimits = normalizeChecklistInputLimits(
      { ...target.checklistInputLimits, [field]: applied },
      false,
    )
    await updateDoc(doc(firestoreDb, 'workspaces', workspaceId), { checklistInputLimits })
    return applied
  }, [normalizedWorkspaces])

  const setWorkspaceLimits = useCallback(async (
    workspaceId: string,
    workspaceLimits: WorkspaceLimits,
  ) => {
    const target = normalizedWorkspaces.find((workspace) => workspace.id === workspaceId)
    if (!target) throw new Error('Workspace not found.')
    if (target.isPreview) throw new Error('Preview workspaces are read-only.')
    const normalized = normalizeWorkspaceLimits(workspaceLimits, workspaceLimits.input, isPersonalWorkspace(target), target.plan, planLimits)
    await updateDoc(doc(firestoreDb, 'workspaces', workspaceId), {
      workspaceLimits: normalized,
      checklistInputLimits: normalized.input,
    })
  }, [normalizedWorkspaces, planLimits])

  const value = useMemo<WorkspaceContextValue>(
    () => {
      const activeWorkspace = normalizedWorkspaces.find((workspace) => workspace.id === activeWorkspaceId)
      const activeRole = resolveActiveRole({
        workspace: activeWorkspace,
        activeWorkspaceId,
        rolesByWorkspaceId,
      })
      const getWorkspaceRole = (workspaceId: string): WorkspaceRole => {
        const workspace = normalizedWorkspaces.find((entry) => entry.id === workspaceId)
        if (isPersonalWorkspace(workspace)) return 'OWNER'
        if (workspace?.isPreview) return 'READONLY'
        return rolesByWorkspaceId.get(workspaceId) ?? 'VIEWER'
      }
      const canPerform = (action: WorkspaceAction) => {
        if (!activeWorkspace) return false
        if (activeWorkspace.isPreview) return roleCanPerform('READONLY', activeWorkspace.actionPermissions[action])
        if (testingOverrideAllowsAllFeatures()) return true
        if (isPersonalWorkspace(activeWorkspace) || isWorkspaceOwner(activeWorkspace, currentUser?.uid)) return true
        return roleCanPerform(activeRole, activeWorkspace.actionPermissions[action])
      }
      const canPerformByRole = (action: WorkspaceAction) => {
        if (!activeWorkspace) return false
        if (activeWorkspace.isPreview) return roleCanPerform('READONLY', activeWorkspace.actionPermissions[action])
        if (isPersonalWorkspace(activeWorkspace) || isWorkspaceOwner(activeWorkspace, currentUser?.uid)) return true
        return roleCanPerform(activeRole, activeWorkspace.actionPermissions[action])
      }
      return ({
      workspaces: normalizedWorkspaces,
      activeWorkspaceId,
      activeRole,
      canPerform,
      canPerformByRole,
      getWorkspaceRole,
      rolesByWorkspaceId,
      appMode,
      currentUserCredits,
      isWorkspaceLoading,
      needsOnboarding,
      workspaceLoadingError,
      activeMemberDefaultView,
      isSidebarPinned,
      setSidebarPinned,
      setActiveWorkspaceId,
      openPreviewWorkspace,
      openPublicWorkspacePreview,
      setAppMode,
      createWorkspace,
      removeWorkspaceFromState,
      refreshWorkspaces: loadWorkspacesForUser,
      setMemberDefaultDashboardView,
      setWorkspaceDashboardViewConfig,
      setWorkspaceChecklistLimit,
      setWorkspaceLimits,
      checkWorkspaceSlugAvailability,
    })},
    [
      activeMemberDefaultView,
      activeWorkspaceId,
      appMode,
      createWorkspace,
      currentUserCredits,
      currentUser?.uid,
      isWorkspaceLoading,
      isSidebarPinned,
      loadWorkspacesForUser,
      needsOnboarding,
      openPreviewWorkspace,
      openPublicWorkspacePreview,
      removeWorkspaceFromState,
      rolesByWorkspaceId,
      setAppMode,
      setActiveWorkspaceId,
      setMemberDefaultDashboardView,
      setWorkspaceDashboardViewConfig,
      setWorkspaceChecklistLimit,
      setWorkspaceLimits,
      setSidebarPinned,
      checkWorkspaceSlugAvailability,
      workspaceLoadingError,
      normalizedWorkspaces,
    ],
  )

  const [isRestoringActiveWorkspace, setIsRestoringActiveWorkspace] = useState(false)
  const activeWorkspace = useMemo(
    () => normalizedWorkspaces.find((w) => w.id === activeWorkspaceId),
    [normalizedWorkspaces, activeWorkspaceId],
  )

  useOpsWorkspaceOfflineLimit(activeWorkspace)

  const isOwnerOfActive = useMemo(
    () => Boolean(activeWorkspace && isWorkspaceOwner(activeWorkspace, currentUser?.uid)),
    [activeWorkspace, currentUser?.uid],
  )

  const [restoreError, setRestoreError] = useState<string | null>(null)

  const handleRestoreActiveWorkspace = useCallback(async () => {
    if (!activeWorkspace?.id) return
    setIsRestoringActiveWorkspace(true)
    setRestoreError(null)
    try {
      await restoreWorkspaceDeletion({ workspaceId: activeWorkspace.id })
      await loadWorkspacesForUser(activeWorkspace.id)
    } catch (err) {
      console.warn('[WorkspaceContext] Could not restore workspace:', err)
      setRestoreError(err instanceof Error ? err.message : 'Could not restore workspace.')
    } finally {
      setIsRestoringActiveWorkspace(false)
    }
  }, [activeWorkspace, loadWorkspacesForUser])

  return (
    <WorkspaceContext.Provider value={value}>
      <WorkspaceDeletionBanner
        activeWorkspace={activeWorkspace}
        isOwnerOfActive={isOwnerOfActive}
        onRestore={handleRestoreActiveWorkspace}
        isRestoring={isRestoringActiveWorkspace}
        restoreError={restoreError}
      />
      {children}
    </WorkspaceContext.Provider>
  )
}

export interface WorkspaceDeletionBannerProps {
  activeWorkspace?: Workspace
  isOwnerOfActive?: boolean
  onRestore: () => Promise<void> | void
  isRestoring?: boolean
  restoreError?: string | null
}

export const WorkspaceDeletionBanner = ({
  activeWorkspace,
  isOwnerOfActive = false,
  onRestore,
  isRestoring = false,
  restoreError = null,
}: WorkspaceDeletionBannerProps) => {
  const { t } = useNls()
  if (activeWorkspace?.deletion?.status !== 'PENDING' || !isOwnerOfActive) {
    return null
  }
  return (
    <div className="workspaceDeletionBanner" style={{ padding: '8px 16px' }}>
      <ControlNotice
        tone="warning"
        title={t('NLSWorkspaceDeletionBannerTitle', { date: formatPurgeDate(activeWorkspace.deletion.purgeAfter) })}
        message={t('NLSDeletionScheduledGrace', { date: formatPurgeDate(activeWorkspace.deletion.purgeAfter) })}
        action={
          <Button
            variant="secondary"
            size="sm"
            onClick={() => void onRestore()}
            loading={isRestoring}
          >
            {t('NLSRestoreWorkspaceAction')}
          </Button>
        }
      />
      {restoreError ? (
        <div style={{ marginTop: '8px' }}>
          <ControlNotice
            tone="danger"
            message={restoreError}
          />
        </div>
      ) : null}
    </div>
  )
}
